import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

import { ApplyWrapper } from '@lib/ui';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';
import type { ColorType } from '@lib/types';

const switchVariants = cva(
  [
    // Layout
    'relative inline-block shrink-0 cursor-pointer appearance-none overflow-hidden',
    // Focus
    'outline-hidden focus:outline-hidden',
    // Transitions
    'transition-all duration-200 ease-in-out',
    // Thumb styling (before pseudo-element)
    'before:absolute before:left-[var(--thumb-border)] before:top-[var(--thumb-border)]',
    'before:h-[calc(100%-(var(--thumb-border)*2))]',
    'before:transition-all before:duration-200 before:ease-in-out',
  ],
  {
    variants: {
      variant: {
        basic: [
          // Unchecked state - track background
          'bg-ui-active',
          // Thumb
          'before:bg-canvas',
          // Checked state
          'checked:bg-this',
          'checked:before:bg-white',
          // Focus ring
          'focus-visible:ring-3 focus-visible:ring-this/50',
        ],
        outlined: [
          // Unchecked state
          'border border-line-hover',
          // Thumb
          'before:bg-fg-muted',
          // Checked state
          'checked:border-this',
          'checked:before:bg-this',
          // Focus ring
          'focus-visible:ring-3 focus-visible:ring-this/50',
        ],
      },
      size: {
        xs: [
          '[--thumb-border:1.5px]',
          'h-4 w-7',
          'before:w-[calc((100%/2)-(var(--thumb-border)*2))]',
          'checked:before:translate-x-[calc(100%+(var(--thumb-border)*2))]',
        ],
        sm: [
          '[--thumb-border:2px]',
          'h-5 w-9',
          'before:w-[calc((100%/2)-(var(--thumb-border)*2))]',
          'checked:before:translate-x-[calc(100%+(var(--thumb-border)*2))]',
        ],
        md: [
          '[--thumb-border:2px]',
          'h-5 w-10',
          'before:w-[calc((100%/2)-(var(--thumb-border)*2))]',
          'checked:before:translate-x-[calc(100%+(var(--thumb-border)*2))]',
        ],
        lg: [
          '[--thumb-border:2.5px]',
          'h-6 w-12',
          'before:w-[calc((100%/2)-(var(--thumb-border)*2))]',
          'checked:before:translate-x-[calc(100%+(var(--thumb-border)*2))]',
        ],
        xl: [
          '[--thumb-border:3px]',
          'h-7 w-14',
          'before:w-[calc((100%/2)-(var(--thumb-border)*2))]',
          'checked:before:translate-x-[calc(100%+(var(--thumb-border)*2))]',
        ],
      },
      disabled: {
        true: [
          // Disabled styling
          'pointer-events-none select-none opacity-60',
          'bg-surface-raised border border-line',
          'before:bg-fg-muted',
          // Adjust thumb for border
          'before:w-[calc((100%/2)-(var(--thumb-border)*2)-1px)]',
          'checked:before:translate-x-[calc(100%+(var(--thumb-border)*2)+2px)]',
        ],
        false: '',
      },
      rounded: {
        default: 'rounded-full before:rounded-full',
        square: 'rounded-lg before:rounded-md',
      },
      thumbInset: {
        default: '', // Uses size variant's default
        xs: '[--thumb-border:1.5px]',
        sm: '[--thumb-border:2px]',
        md: '[--thumb-border:3px]',
        lg: '[--thumb-border:4px]',
        xl: '[--thumb-border:5px]',
        '2xl': '[--thumb-border:6px]',
      },
    },
    compoundVariants: [
      // Outlined variant needs adjusted thumb width for border
      {
        variant: 'outlined',
        disabled: false,
        className: [
          'before:w-[calc((100%/2)-(var(--thumb-border)*2)-1px)]',
          'checked:before:translate-x-[calc(100%+(var(--thumb-border)*2)+2px)]',
        ],
      },
    ],
    defaultVariants: {
      variant: 'basic',
      size: 'md',
      disabled: false,
      rounded: 'default',
      thumbInset: 'default',
    },
  }
);

const labelVariants = cva(['inline-flex items-center gap-2 cursor-pointer'], {
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-60',
      false: '',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

type SwitchProps = {
  color?: Exclude<ColorType, 'neutral'>;
  label?: ReactNode;
  classNames?: {
    input?: string;
    label?: string;
    labelText?: string;
  };
  labelProps?: Record<string, unknown>;
} & VariantProps<typeof switchVariants> &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>;

const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      variant = 'basic',
      size = 'md',
      rounded = 'default',
      thumbInset = 'default',
      color = 'primary',
      className,
      classNames = {},
      label,
      disabled = false,
      labelProps,
      ...rest
    },
    ref
  ) => {
    return (
      <ApplyWrapper
        when={!!label}
        wrapper={(children) => (
          <label className={cn(labelVariants({ disabled }), classNames?.label)} {...labelProps}>
            {children}
            <span className={cn('select-none', classNames?.labelText)}>{label}</span>
          </label>
        )}
      >
        <input
          className={cn(
            switchVariants({ variant, size, disabled, rounded, thumbInset }),
            setThisClass(color),
            className,
            classNames?.input
          )}
          disabled={disabled}
          data-disabled={disabled || undefined}
          type="checkbox"
          role="switch"
          ref={ref}
          {...rest}
        />
      </ApplyWrapper>
    );
  }
);

Switch.displayName = 'Switch';

export { Switch };
export type { SwitchProps };
